import { mmToPx2, PAGE_HEIGHT_IN_PX, PAGE_WIDTH_IN_PX } from './coverGeometry'

// 표지 데이터의 실제 타입은 domain 의 DesignTemplate 이 갖는다. shared 는 domain 을 볼 수 없으므로
// 여기 함수가 읽고 고치는 필드만 담은 최소 모양으로 받는다.
type CoverTextItemLike = {
  id: string
  type: 'TEXT' | 'TO_LOCALE_STRING'
  props: {
    hidden?: boolean
    renderProps: {
      x?: number | string
      y?: number | string
      transform?: string
      maxWidth?: number
      height?: number
      fontSize: number
    }
  }
}

type CoverBoxItemLike = {
  id: string
  type: 'IMAGE' | 'EDITABLE_IMAGE' | 'RECT' | 'LINE'
  props: {
    x?: number | string
    width?: number | string
    transform?: string
  }
}

export type CoverItemLike = CoverTextItemLike | CoverBoxItemLike

const isTextItem = (item: CoverItemLike): item is CoverTextItemLike =>
  item.type === 'TEXT' || item.type === 'TO_LOCALE_STRING'

export type CoverDataLike = {
  customData: CoverItemLike[]
  sideTitle?: CoverTextItemLike | null
  width?: string | null
  height?: string | null
}

// 책등(앞면과 뒷면 사이) 영역을 그리는 customData 사각형의 id
export const COVER_BINDING_AREA_RECT_ID = 'COVER_BINDING_AREA'

const NON_SLOT_KEYS = ['customData', 'width', 'height'] as const

type CoverSlotKey<T> = Exclude<keyof T, (typeof NON_SLOT_KEYS)[number]>
const isSlotKey = (key: string) => !(NON_SLOT_KEYS as readonly string[]).includes(key)
export type CoverItemOf<T extends { customData: readonly unknown[] }> =
  | T['customData'][number]
  | NonNullable<T[CoverSlotKey<T>]>

/** customData 와 고정 칸의 항목을 모두 나열한다. 비어 있는 칸은 뺀다 */
export const getAllCoverItems = <T extends { customData: readonly unknown[] }>(
  coverData: T,
): CoverItemOf<T>[] =>
  [
    ...coverData.customData,
    ...Object.entries(coverData)
      .filter(([key]) => isSlotKey(key))
      .map(([, value]) => value),
  ].filter(Boolean) as CoverItemOf<T>[]

/** 고정 칸 이름이면 그 칸을, 아니면 같은 id 의 customData 항목을 찾는다 */
export const findCoverItem = <T extends { customData: readonly { id: string }[] }>(
  coverData: T,
  id: string,
): CoverItemOf<T> | null | undefined => {
  if (id in coverData && isSlotKey(id)) {
    return (coverData as Record<string, unknown>)[id] as CoverItemOf<T> | null
  }
  return coverData.customData.find((item) => item.id === id) as CoverItemOf<T> | undefined
}

const calculateSideRectWidthInMm = (pages: number) => {
  const a = 0.00000268
  const b = 0.0446748
  const c = 0.0574
  return a * pages * pages + b * pages + c
}

const parseRotateDegree = (transform: string): number | undefined => {
  const match = transform.match(/rotate\((\d+\.?\d*)\s/)
  if (match) {
    return parseFloat(match[1])
  }
}

const rotatePoint = (x: number, y: number, angle: number) => {
  const rad = -angle * (Math.PI / 180)

  let newX = x * Math.cos(rad) - y * Math.sin(rad)
  let newY = x * Math.sin(rad) + y * Math.cos(rad)

  // 부동소수 오차로 생긴 아주 작은 값은 0 으로 본다
  newX = Math.abs(newX) < 1e-10 ? 0 : newX
  newY = Math.abs(newY) < 1e-10 ? 0 : newY

  return { x: newX, y: newY }
}

// translate(...) 가 여러 번 붙어 있으면 마지막 하나만 남긴다. 같은 표지를 여러 번 조정해도 이동이 쌓이지 않는다
const keepLastTranslate = (transform: string) => {
  const translateRegex = /translate\([^)]+\)/g
  const translates = transform.match(translateRegex)

  if (translates && translates.length > 0) {
    const lastTranslate = translates[translates.length - 1]
    const prefix = transform.replace(translateRegex, '').trim()
    return `${prefix} ${lastTranslate}`.trim()
  }

  return transform
}

const MIN_PAGE_COUNT_FOR_SIDE_TITLE = 41

/**
 * 쪽수에 맞춰 책등 너비를 정하고, 책등 제목을 세로로 맞추고, 앞면 항목을 책등 너비만큼 오른쪽으로 민다.
 * 받은 표지 데이터를 제자리에서 고치고 그 객체를 돌려준다 — 호출부가 고친 원본을 계속 쓴다.
 */
export const adjustSpineWithPageSize = <T extends CoverDataLike>({
  coverData,
  pageSize,
}: {
  coverData: T
  pageSize: number | string
}): T => {
  const maxPage = Number(pageSize)
  const sideRectWidthInMm = calculateSideRectWidthInMm(maxPage) + 0.9
  const sideRectWidthInPx = mmToPx2(sideRectWidthInMm)
  const paddingInMm = maxPage / 35
  const fontSize = (sideRectWidthInMm - paddingInMm) * 4 - 2.8

  const sideRect = coverData.customData.find((item) => item.id === COVER_BINDING_AREA_RECT_ID)
  if (sideRect && sideRect.type === 'RECT') {
    sideRect.props.width = sideRectWidthInPx
  }

  const sideTitle = coverData.sideTitle
  if (sideTitle) {
    if (maxPage < MIN_PAGE_COUNT_FOR_SIDE_TITLE) {
      sideTitle.props.hidden = true
    }

    const sideTitleWidth = parseInt(String(sideTitle.props.renderProps.maxWidth))
    const centerX = PAGE_WIDTH_IN_PX + sideRectWidthInPx / 2
    const centerY = PAGE_HEIGHT_IN_PX / 2 - sideRectWidthInPx / 2

    sideTitle.props.renderProps.height = fontSize
    sideTitle.props.renderProps.fontSize = fontSize
    sideTitle.props.renderProps.x = centerX - sideTitleWidth / 2 - sideRectWidthInPx / 2
    sideTitle.props.renderProps.y = centerY - fontSize / 1.8
    sideTitle.props.renderProps.transform = `rotate(90 ${centerX} ${centerY})`
  }

  coverData.width = String(sideRectWidthInPx + PAGE_WIDTH_IN_PX * 2)
  coverData.height = String(PAGE_HEIGHT_IN_PX)

  const items: CoverItemLike[] = getAllCoverItems<CoverDataLike>(coverData)
  items.forEach((item) => {
    const box = isTextItem(item)
      ? {
          transform: item.props.renderProps.transform,
          x: item.props.renderProps.x,
          width: item.props.renderProps.maxWidth,
        }
      : item.props
    const transform = box.transform ?? ''
    const x2 = Number(box.x) + Number(box.width)

    let translate = `translate(${sideRectWidthInPx}, 0)`

    // 책등 제목은 위에서 따로 회전시켰으므로 회전 보정에서 뺀다
    if (item.id !== 'sideTitle' && transform.includes('rotate')) {
      const degree = parseRotateDegree(transform)
      if (degree !== undefined) {
        const { x, y } = rotatePoint(sideRectWidthInPx, 0, degree)
        translate = `translate(${x}, ${y})`
      }
    }

    // 끝점(x2)이 앞면에 있는 항목만 민다. 뒷면·책등에서 시작해 앞면으로 넘어오는 항목도 있어 시작점이 아닌 끝점을 본다
    if (x2 > PAGE_WIDTH_IN_PX + sideRectWidthInPx && item.id !== COVER_BINDING_AREA_RECT_ID) {
      const translated = keepLastTranslate(`${transform ? transform + ' ' : ''}${translate}`)
      if (isTextItem(item)) {
        item.props.renderProps.transform = translated
      } else {
        item.props.transform = translated
      }
    }
  })

  return coverData
}
